/**
 * Merge a freshly loaded batch of messages into the existing message array and
 * return the next array. Returns the **same reference** when nothing changed,
 * so callers (and any watchers downstream) can skip no-op re-renders — most
 * poll ticks return an unchanged thread.
 *
 * The optional matchOptimistic(optimistic, incoming) predicate decides whether
 * an incoming server message is the persisted counterpart of an unconfirmed
 * optimistic message (e.g. matching on externalId, or on body when the API
 * doesn't echo a correlation id).
 *
 * Note: not a pure function — matched optimistic messages are mutated in
 * place (server fields copied on, stale `error` cleared), because callers
 * render those exact object references and rely on them being updated.
 */
export function mergeMessages(existing, incoming, {matchOptimistic} = {}) {

  //If there are no messages yet, set all of them directly (skip the no-op
  //empty-to-empty assignment so an idle poll doesn't fire array watchers)
  if (existing.length === 0) {
    return incoming.length > 0 ? incoming : existing
  }

  //Reconcile optimistic user messages with their persisted counterparts so a
  //poll that races the create request doesn't duplicate them. Match an
  //unconfirmed incoming message to the first server message satisfying the
  //feature's matchOptimistic predicate that we haven't already adopted.
  let hasAdoptedMessages = false
  if (matchOptimistic) {
    existing
      .filter(message => message.isNew && message.isIncoming)
      .forEach(message => {
        const match = incoming.find(m =>
          m.id &&
          !existing.some(e => e.id === m.id) &&
          matchOptimistic(message, m)
        )
        if (match) {
          Object.assign(message, match)

          //The server copy proves delivery, so clear a stale send error (e.g.
          //the create request timed out after the backend persisted the message)
          message.error = null
          hasAdoptedMessages = true
        }
      })
  }

  //Now find out which messages are new and append them. Only return a new array
  //(and thereby trigger watchers and re-renders downstream) when this batch
  //actually changed something.
  const existingIds = new Set(existing.map(message => message.id))
  const newMessages = incoming.filter(message => !existingIds.has(message.id))
  if (newMessages.length > 0 || hasAdoptedMessages) {
    return [...existing, ...newMessages]
  }
  return existing
}
